import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
	api,
	type NotificationCampaign,
	type NotificationCampaignDraftRequest,
	type NotificationCampaignSendRequest,
	type WeeklyDigestSettings,
} from "@/api";
import { useAuth } from "@/store";

export const notificationKeys = {
	all: (userId: string) => ["notifications", userId] as const,
	list: (userId: string, locale: string, filter: string) => ["notifications", userId, "list", locale, filter] as const,
	count: (userId: string) => ["notifications", userId, "count"] as const,
};

export const campaignKeys = {
	all: ["notificationCampaigns"] as const,
	list: (status?: string) => ["notificationCampaigns", "list", status ?? "all"] as const,
	detail: (id: string) => ["notificationCampaigns", "detail", id] as const,
	stats: (id: string) => ["notificationCampaigns", "stats", id] as const,
	recipients: (search: string) => ["notificationCampaigns", "recipients", search] as const,
};

const localeOf = (language?: string): "ru" | "en" => language?.startsWith("en") ? "en" : "ru";

export function useNotifications(filter: "all" | "unread" = "all") {
	const userId = useAuth((state) => state.user?.id) ?? "anonymous";
	const { i18n } = useTranslation();
	const locale = localeOf(i18n.resolvedLanguage);
	return useInfiniteQuery({
		queryKey: notificationKeys.list(userId, locale, filter),
		queryFn: ({ pageParam }) => api.notifications.list({ cursor: pageParam, limit: 20, filter, locale }),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (page) => page.nextCursor ?? undefined,
		enabled: userId !== "anonymous",
	});
}

export function useNotificationCount() {
	const userId = useAuth((state) => state.user?.id) ?? "anonymous";
	return useQuery({
		queryKey: notificationKeys.count(userId),
		queryFn: () => api.notifications.unreadCount(),
		enabled: userId !== "anonymous",
		refetchInterval: 30_000,
		refetchIntervalInBackground: false,
	});
}

export function useNotificationReadActions() {
	const queryClient = useQueryClient();
	const userId = useAuth((state) => state.user?.id) ?? "anonymous";
	const invalidate = () => queryClient.invalidateQueries({ queryKey: notificationKeys.all(userId) });
	return {
		markRead: useMutation({ mutationFn: (id: string) => api.notifications.markRead(id), onSuccess: invalidate }),
		markAll: useMutation({ mutationFn: () => api.notifications.markAllRead(), onSuccess: invalidate }),
	};
}

export function useCampaigns(status?: string) {
	return useInfiniteQuery({
		queryKey: campaignKeys.list(status),
		queryFn: ({ pageParam }) => api.notificationCampaigns.list({
			cursor: pageParam,
			limit: 25,
			status: status && status !== "all" ? status as NotificationCampaign["status"] : undefined,
		}),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (page) => page.nextCursor ?? undefined,
	});
}

export function useCampaign(id?: string) {
	return useQuery({
		queryKey: campaignKeys.detail(id ?? ""),
		queryFn: () => api.notificationCampaigns.get(id ?? ""),
		enabled: Boolean(id),
		refetchInterval: (query) => ["queued", "processing", "scheduled"].includes(query.state.data?.status ?? "") ? 7_000 : false,
	});
}

export function useCampaignStats(id?: string) {
	return useQuery({
		queryKey: campaignKeys.stats(id ?? ""),
		queryFn: () => api.notificationCampaigns.stats(id ?? ""),
		enabled: Boolean(id),
	});
}

export function useRecipientSearch(search: string) {
	return useQuery({
		queryKey: campaignKeys.recipients(search),
		queryFn: () => api.notificationCampaigns.recipients({ search, limit: 20 }),
		enabled: search.trim().length >= 2,
		staleTime: 30_000,
	});
}

/** «Похожие подборки недели» (fd-notif-01): настройка площадки. */
export function useWeeklyDigestSettings() {
	const queryClient = useQueryClient();
	const key = ["notification-campaigns", "weekly-digest"];
	const query = useQuery({ queryKey: key, queryFn: () => api.notificationCampaigns.weeklyDigest() });
	const save = useMutation({
		mutationFn: (input: WeeklyDigestSettings) => api.notificationCampaigns.updateWeeklyDigest(input),
		onSuccess: (data) => queryClient.setQueryData(key, data),
	});
	return { query, save };
}

export function useCampaignActions() {
	const queryClient = useQueryClient();
	const refresh = () => queryClient.invalidateQueries({ queryKey: campaignKeys.all });
	return {
		create: useMutation({ mutationFn: (input: NotificationCampaignDraftRequest) => api.notificationCampaigns.create(input), onSuccess: refresh }),
		preview: useMutation({ mutationFn: ({ id, locale }: { id: string; locale?: "ru" | "en" }) => api.notificationCampaigns.preview(id, locale), onSuccess: refresh }),
		send: useMutation({ mutationFn: ({ id, input }: { id: string; input: NotificationCampaignSendRequest }) => api.notificationCampaigns.send(id, input), onSuccess: refresh }),
		cancel: useMutation({ mutationFn: (id: string) => api.notificationCampaigns.cancel(id), onSuccess: refresh }),
	};
}
